import React, { FC } from 'react'
import styled from 'styled-components'

import { getProjectName } from '../../lib/get-project-name'

export type Props = {
  nonSemantic?: boolean
  weight?: 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900
  typographic?: boolean
}

export type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export const H1: FC<Props> = ({ nonSemantic = false, weight = 300, typographic = false, children, ...restProps }) => {
  return (
    <StyledH1 weight={weight} as={nonSemantic ? 'span' : 'h1'} typographic={typographic} {...restProps}>
      {children}
    </StyledH1>
  )
}

export const H2: FC<Props> = ({ nonSemantic = false, weight = 300, typographic = false, children, ...restProps }) => {
  return (
    <StyledH2 weight={weight} as={nonSemantic ? 'span' : 'h2'} typographic={typographic} {...restProps}>
      {children}
    </StyledH2>
  )
}

export const H3: FC<Props> = ({ nonSemantic = false, weight = 300, typographic = false, children, ...restProps }) => {
  return (
    <StyledH3 weight={weight} as={nonSemantic ? 'span' : 'h3'} typographic={typographic} {...restProps}>
      {children}
    </StyledH3>
  )
}

export const H4: FC<Props> = ({ nonSemantic = false, weight = 300, typographic = false, children, ...restProps }) => {
  return (
    <StyledH4 weight={weight} as={nonSemantic ? 'span' : 'h4'} typographic={typographic} {...restProps}>
      {children}
    </StyledH4>
  )
}

export const H5: FC<Props> = ({ nonSemantic = false, weight = 300, typographic = false, children, ...restProps }) => {
  return (
    <StyledH5 weight={weight} as={nonSemantic ? 'span' : 'h5'} typographic={typographic} {...restProps}>
      {children}
    </StyledH5>
  )
}

export const H6: FC<Props> = ({ nonSemantic = false, weight = 300, typographic = false, children, ...restProps }) => {
  return (
    <StyledH6 weight={weight} as={nonSemantic ? 'span' : 'h6'} typographic={typographic} {...restProps}>
      {children}
    </StyledH6>
  )
}

const StyledHeading = styled.span<Props>`
  font-weight: ${({ weight }) => weight + ' !important'};
  ${({ typographic }) => !typographic && `margin: 0!important`};
  ${() => {
    if (getProjectName() === 'sollers') {
      return `
        font-size: 2.4rem!important;
        color: #292523;!important;
        font-weight: 500!important;
      `
    }
    return null
  }};
  font-size: ${() => {
    return getProjectName() === 'haval' ? '1.4rem!important' : null
  }};
  ${() => {
    if (getProjectName() === 'cctruck') {
      return `
        font-family: Manrope,-apple-system,BlinkMacSystemFont,"Segoe UI","Roboto" ,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"!important;
        font-size: 18px!important;
        font-weight: 600!important;
        line-height: 25px!important;
        
        @media screen and (max-width: 1024px) {
          font-size: 16px!important;
          line-height: 22px!important;
        }
      `
    }
    return null
  }};
`

const StyledH1 = styled(StyledHeading)<Props>`
  ${({ theme }) => theme.typography.title.h1};
`

const StyledH2 = styled(StyledHeading)<Props>`
  ${({ theme }) => theme.typography.title.h2};
`

const StyledH3 = styled(StyledHeading)<Props>`
  ${({ theme }) => theme.typography.title.h3}
`

const StyledH4 = styled(StyledHeading)<Props>`
  ${({ theme }) => theme.typography.title.h4};
`

const StyledH5 = styled(StyledHeading)<Props>`
  ${({ theme }) => theme.typography.title.h5};
`

const StyledH6 = styled(StyledHeading)`
  ${({ theme }) => theme.typography.title.h6};
`

export type HeadingByTagProps = {
  tag?: HeadingTag
  semantic?: boolean
  value?: string
  color?: {
    r: number
    g: number
    b: number
    a: number
  }
  visual_tag?: string
  text?: string
  align?: 'left' | 'center' | 'right'
  weight?: number
  paddings?: null | {
    top: number
    bottom: number
    left: number
    right: number
  }
}

export const HeadingByTag: FC<HeadingByTagProps> = ({ tag = 'h1', children, semantic }) => {
  if (tag === 'h1') {
    return (
      <H1 nonSemantic={!semantic} typographic={true}>
        {children}
      </H1>
    )
  } else if (tag === 'h2') {
    return (
      <H2 nonSemantic={!semantic} typographic={true}>
        {children}
      </H2>
    )
  } else if (tag === 'h3') {
    return (
      <H3 nonSemantic={!semantic} typographic={true}>
        {children}
      </H3>
    )
  } else if (tag === 'h4') {
    return (
      <H4 nonSemantic={!semantic} typographic={true}>
        {children}
      </H4>
    )
  } else if (tag === 'h5') {
    return (
      <H5 nonSemantic={!semantic} typographic={true}>
        {children}
      </H5>
    )
  } else if (tag === 'h6') {
    return (
      <H6 nonSemantic={!semantic} typographic={true}>
        {children}
      </H6>
    )
  } else {
    console.error('Tag doesn`t exist')
    return null
  }
}
